import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

type CourseItem = {
  id: number;
  name: string;
};

type FreeResourceItem = {
  id: number;
  title: string;
  description: string;
  gradeLabel: string;
  fileUrl: string;
  isActive: boolean;
  sortOrder: number;
  courseIds: number[];
};

type FormState = {
  title: string;
  description: string;
  gradeLabel: string;
  fileUrl: string;
  isActive: boolean;
  sortOrder: number;
  courseIds: number[];
};

const emptyForm: FormState = {
  title: '',
  description: '',
  gradeLabel: '',
  fileUrl: '',
  isActive: true,
  sortOrder: 0,
  courseIds: [],
};

const ManageFreeResources: React.FC = () => {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [resources, setResources] = useState<FreeResourceItem[]>([]);
  const [courses, setCourses] = useState<CourseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);

  const apiBase = `${config.apiBaseUrl}/api`;

  const getApiToken = async () => {
    if (!isAuthenticated) {
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      await loginWithRedirect({ appState: { returnTo } });
      throw new Error('Please login to continue.');
    }

    try {
      return await getAccessTokenSilently();
    } catch {
      return await getAccessTokenSilently({ cacheMode: 'off' } as any);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const [resourceResponse, courseResponse] = await Promise.all([
          axios.get<FreeResourceItem[]>(`${apiBase}/freeresources/admin`),
          axios.get<CourseItem[]>(`${apiBase}/courses`),
        ]);

        setResources(Array.isArray(resourceResponse.data) ? resourceResponse.data : []);
        setCourses(Array.isArray(courseResponse.data) ? courseResponse.data : []);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load free resources');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [apiBase, getAccessTokenSilently]);

  const filteredResources = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return resources;

    return resources.filter((item) => {
      return (
        item.title.toLowerCase().includes(q) ||
        item.gradeLabel.toLowerCase().includes(q) ||
        item.fileUrl.toLowerCase().includes(q)
      );
    });
  }, [resources, searchText]);

  const startAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setForm(emptyForm);
  };

  const startEdit = (item: FreeResourceItem) => {
    setIsAdding(false);
    setEditingId(item.id);
    setForm({
      title: item.title,
      description: item.description,
      gradeLabel: item.gradeLabel,
      fileUrl: item.fileUrl,
      isActive: item.isActive,
      sortOrder: item.sortOrder,
      courseIds: item.courseIds || [],
    });
  };

  const cancelEdit = () => {
    setIsAdding(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const toggleCourse = (courseId: number) => {
    setForm((prev) => {
      const exists = prev.courseIds.includes(courseId);
      return {
        ...prev,
        courseIds: exists ? prev.courseIds.filter((id) => id !== courseId) : [...prev.courseIds, courseId],
      };
    });
  };

  const saveResource = async () => {
    if (!form.title.trim() || !form.fileUrl.trim()) {
      setError('Title and file URL are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const token = await getApiToken();

      const payload = {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        gradeLabel: form.gradeLabel.trim(),
        fileUrl: form.fileUrl.trim(),
        sortOrder: Number(form.sortOrder) || 0,
      };

      if (isAdding) {
        await axios.post(`${apiBase}/freeresources`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else if (editingId) {
        await axios.put(`${apiBase}/freeresources/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      const refreshed = await axios.get<FreeResourceItem[]>(`${apiBase}/freeresources/admin`);
      setResources(Array.isArray(refreshed.data) ? refreshed.data : []);
      cancelEdit();
    } catch (err: any) {
      console.error('Error saving free resource:', err);
      setError(err?.response?.data?.message || 'Failed to save free resource');
    } finally {
      setSaving(false);
    }
  };

  const deleteResource = async (id: number) => {
    try {
      setError(null);
      const token = await getApiToken();
      await axios.delete(`${apiBase}/freeresources/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResources((prev) => prev.filter((item) => item.id !== id));
      if (editingId === id) cancelEdit();
    } catch (err: any) {
      console.error('Error deleting free resource:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to delete free resource');
    }
  };

  return (
    <div className="bg-gradient-to-br from-app-page-from to-app-page-to" style={{ width: '100%', minHeight: '100vh' }}>
      <div className="app-breadcrumb-bar">
        <div className="app-breadcrumb-row">
          <button onClick={() => navigate('/')} className="app-breadcrumb-link">Home</button>
          <span className="app-breadcrumb-sep">›</span>
          <span className="app-breadcrumb-current">Manage Free Resources</span>
        </div>
      </div>

      <div className="p-3 md:p-6">
        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-left text-sm text-red-700">{error}</div>
        )}

        <div className="rounded-lg bg-white shadow-md">
          <div className="p-4 md:p-5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-t-lg">
            <div className="flex items-center justify-between gap-3">
              <div className="text-left">
                <h1 className="text-xl md:text-2xl font-bold">Manage Free Resources</h1>
                <p className="text-xs md:text-sm text-white/80">Create, edit, and map downloadable resources to courses</p>
              </div>
              <button
                onClick={startAdd}
                className="rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/30"
              >
                + Add Resource
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr]">
            <div className="border-r border-slate-100 p-4">
              <input
                type="text"
                placeholder="Search resources..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />

              <div className="mt-3 max-h-[70vh] overflow-auto space-y-2">
                {loading ? (
                  <div className="text-sm text-slate-600">Loading...</div>
                ) : filteredResources.length === 0 ? (
                  <div className="text-sm text-slate-600">No resources found.</div>
                ) : (
                  filteredResources.map((item) => {
                    const selected = editingId === item.id;
                    return (
                      <div
                        key={item.id}
                        className={`rounded-lg border p-3 text-left ${selected ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}
                      >
                        <button className="w-full text-left" onClick={() => startEdit(item)}>
                          <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                          <p className="mt-1 text-xs text-slate-500">{item.gradeLabel || 'No level label'}</p>
                          <p className="mt-1 text-[11px] text-slate-500">Sort: {item.sortOrder} • {item.isActive ? 'Active' : 'Inactive'}</p>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            void deleteResource(item.id);
                          }}
                          className="mt-2 rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="p-4 md:p-5 text-left">
              {(isAdding || editingId) ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600">Title</label>
                    <input
                      value={form.title}
                      onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600">Description</label>
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600">Grade Label</label>
                      <input
                        value={form.gradeLabel}
                        onChange={(e) => setForm((prev) => ({ ...prev, gradeLabel: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600">Sort Order</label>
                      <input
                        type="number"
                        value={form.sortOrder}
                        onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: Number(e.target.value) || 0 }))}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600">File URL</label>
                    <input
                      value={form.fileUrl}
                      onChange={(e) => setForm((prev) => ({ ...prev, fileUrl: e.target.value }))}
                      placeholder="/downloads/my-file.pdf or https://..."
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      id="resource-active"
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    />
                    <label htmlFor="resource-active" className="text-sm text-slate-700">Active</label>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-slate-600">Associate Courses</p>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-52 overflow-auto rounded-lg border border-slate-200 p-3">
                      {courses.map((course) => (
                        <label key={course.id} className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={form.courseIds.includes(course.id)}
                            onChange={() => toggleCourse(course.id)}
                          />
                          <span>{course.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={saveResource}
                      disabled={saving}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                  Select a resource from the list or click Add Resource.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageFreeResources;
